import { useState, useEffect } from "react";
import axios from "axios";
import {
  Plus, Edit3, Trash2, BookOpen, ChevronDown, ChevronRight, X, Save,
  Eye, EyeOff, Lock, Unlock, Loader2, FileText, ListOrdered, Hash
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const EMPTY_MODULE = {
  slug: "", title: "", summary: "", order: 0, estimated_minutes: 60, is_premium: false
};
const EMPTY_LESSON = {
  module_id: "", slug: "", title: "", order: 0,
  body_md: "", starter_code: "# Your code here\n",
  visible_tests: "", hidden_tests: "",
  hints: [{ level: 1, text: "" }, { level: 2, text: "" }, { level: 3, text: "" }, { level: 4, text: "" }],
  xp_reward: 10, tags: [], is_premium: false
};

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-dm-sans font-bold text-gray-700 mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-gray-500 font-dm-sans mt-1">{hint}</div>}
    </label>
  );
}

const Input = (props) => (
  <input {...props} className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm font-dm-sans focus:border-black outline-none" />
);
const Textarea = (props) => (
  <textarea {...props} className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono focus:border-black outline-none" />
);

function ModuleEditor({ open, item, onClose, onSaved }) {
  const isEdit = !!item?.module_id;
  const [form, setForm] = useState(item || EMPTY_MODULE);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { setForm(item || EMPTY_MODULE); setErr(""); }, [item, open]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      if (isEdit) {
        await axios.put(`${API}/python-lab/admin/modules/${form.module_id}`, form, authHeader());
      } else {
        await axios.post(`${API}/python-lab/admin/modules`, form, authHeader());
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-outfit font-bold text-lg">{isEdit ? "Edit Module" : "New Module"}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Title"><Input value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
            <Field label="Slug" hint="URL-safe id (e.g., python-basics)"><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} /></Field>
          </div>
          <Field label="Summary"><Textarea rows={2} value={form.summary} onChange={(e) => set("summary", e.target.value)} /></Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Order"><Input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} /></Field>
            <Field label="Est. Minutes"><Input type="number" value={form.estimated_minutes} onChange={(e) => set("estimated_minutes", parseInt(e.target.value) || 0)} /></Field>
            <Field label="Premium">
              <button type="button" onClick={() => set("is_premium", !form.is_premium)} className={`w-full flex items-center gap-2 border rounded px-3 py-2 text-sm font-dm-sans font-bold ${form.is_premium ? "border-amber-400 text-amber-600 bg-amber-50" : "border-gray-300 text-gray-600"}`}>
                {form.is_premium ? <Lock size={14} /> : <Unlock size={14} />}
                {form.is_premium ? "Premium" : "Free"}
              </button>
            </Field>
          </div>
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-dm-sans font-bold text-gray-600">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 bg-black text-white text-sm font-dm-sans font-bold rounded inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function LessonEditor({ open, item, modules, onClose, onSaved, defaultModuleId }) {
  const isEdit = !!item?.lesson_id;
  const [form, setForm] = useState(item || { ...EMPTY_LESSON, module_id: defaultModuleId || "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setForm(item || { ...EMPTY_LESSON, module_id: defaultModuleId || (modules[0]?.module_id || "") });
    setErr("");
  }, [item, open, defaultModuleId, modules]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setHint = (level, text) => {
    const h = [...(form.hints || [])];
    const idx = h.findIndex((x) => x.level === level);
    if (idx >= 0) h[idx] = { level, text };
    else h.push({ level, text });
    h.sort((a, b) => a.level - b.level);
    set("hints", h);
  };
  const getHint = (level) => (form.hints || []).find((h) => h.level === level)?.text || "";

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      const payload = { ...form, hints: form.hints || [] };
      if (typeof payload.tags === "string") {
        payload.tags = payload.tags.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (isEdit) {
        await axios.put(`${API}/python-lab/admin/lessons/${form.lesson_id}`, payload, authHeader());
      } else {
        await axios.post(`${API}/python-lab/admin/lessons`, payload, authHeader());
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const tagsValue = Array.isArray(form.tags) ? form.tags.join(", ") : (form.tags || "");

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="font-outfit font-bold text-lg">{isEdit ? "Edit Lesson" : "New Lesson"}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Module">
              <select value={form.module_id} onChange={(e) => set("module_id", e.target.value)}
                className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm font-dm-sans">
                {modules.map((m) => (
                  <option key={m.module_id} value={m.module_id}>{m.title}</option>
                ))}
              </select>
            </Field>
            <Field label="Title"><Input value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
            <Field label="Slug"><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} /></Field>
            <Field label="Order"><Input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} /></Field>
            <Field label="XP Reward"><Input type="number" value={form.xp_reward} onChange={(e) => set("xp_reward", parseInt(e.target.value) || 10)} /></Field>
            <Field label="Tags (comma separated)"><Input value={tagsValue} onChange={(e) => set("tags", e.target.value)} /></Field>
          </div>

          <Field label="Body (Markdown)"><Textarea rows={9} value={form.body_md} onChange={(e) => set("body_md", e.target.value)} /></Field>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Field label="Starter code"><Textarea rows={6} value={form.starter_code} onChange={(e) => set("starter_code", e.target.value)} /></Field>
            <Field label="Visible tests" hint="Shown to learner. Functions named test_* will run."><Textarea rows={6} value={form.visible_tests} onChange={(e) => set("visible_tests", e.target.value)} /></Field>
            <Field label="Hidden tests" hint="Used for grading; hidden from learner UI."><Textarea rows={6} value={form.hidden_tests} onChange={(e) => set("hidden_tests", e.target.value)} /></Field>
          </div>

          <div>
            <div className="text-xs font-dm-sans font-bold text-gray-700 mb-2">Hints (4 levels)</div>
            <div className="space-y-2">
              {[1, 2, 3, 4].map((lvl) => (
                <div key={lvl} className="flex items-start gap-2">
                  <span className="text-[10px] font-mono font-bold text-gray-500 mt-2 w-6 flex-shrink-0">L{lvl}</span>
                  <Textarea rows={2} value={getHint(lvl)} onChange={(e) => setHint(lvl, e.target.value)}
                    placeholder={
                      lvl === 1 ? "Socratic question — make them think (no code)" :
                      lvl === 2 ? "Name the concept (no code)" :
                      lvl === 3 ? "Pseudo-code or one-line snippet" :
                      "Full solution + explanation"
                    } />
                </div>
              ))}
            </div>
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm font-dm-sans font-bold text-gray-600">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 bg-black text-white text-sm font-dm-sans font-bold rounded inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Lesson
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPythonLab() {
  const [modules, setModules] = useState([]);
  const [lessonsByModule, setLessonsByModule] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModuleEditor, setShowModuleEditor] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [showLessonEditor, setShowLessonEditor] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [defaultModuleId, setDefaultModuleId] = useState(null);

  const loadModules = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/python-lab/admin/modules`, authHeader());
      setModules(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  const loadLessons = async (module_id) => {
    const r = await axios.get(`${API}/python-lab/admin/lessons?module_id=${module_id}`, authHeader());
    setLessonsByModule((s) => ({ ...s, [module_id]: r.data || [] }));
  };

  useEffect(() => { loadModules(); }, []);

  const toggleExpand = async (m) => {
    if (expanded[m.module_id]) {
      setExpanded((s) => ({ ...s, [m.module_id]: false }));
    } else {
      if (!lessonsByModule[m.module_id]) await loadLessons(m.module_id);
      setExpanded((s) => ({ ...s, [m.module_id]: true }));
    }
  };

  const deleteModule = async (m) => {
    if (!confirm(`Delete module "${m.title}"?`)) return;
    try {
      await axios.delete(`${API}/python-lab/admin/modules/${m.module_id}`, authHeader());
      loadModules();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const deleteLesson = async (l) => {
    if (!confirm(`Delete lesson "${l.title}"?`)) return;
    await axios.delete(`${API}/python-lab/admin/lessons/${l.lesson_id}`, authHeader());
    loadLessons(l.module_id);
    loadModules();
  };

  return (
    <div className="px-6 py-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">Python Lab</h1>
          <p className="text-sm text-gray-500 font-dm-sans mt-1">Manage modules, lessons, tests and hints for the Python Lab AI tool</p>
        </div>
        <button
          data-testid="add-module-btn"
          onClick={() => { setEditingModule(null); setShowModuleEditor(true); }}
          className="flex items-center gap-2 bg-black text-white px-4 py-2.5 text-sm font-dm-sans font-bold hover:bg-gray-800 transition-colors rounded-md"
        >
          <Plus size={16} /> Add Module
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="text-gray-400 animate-spin" />
        </div>
      )}

      <div className="space-y-3">
        {modules.map((m) => (
          <div key={m.module_id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors">
              <button onClick={() => toggleExpand(m)} className="text-gray-500 hover:text-gray-900" data-testid={`expand-${m.slug}`}>
                {expanded[m.module_id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              <span className="text-[10px] font-mono font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">M{String(m.order).padStart(2, "0")}</span>
              <div className="flex-1 min-w-0">
                <div className="font-outfit font-bold text-gray-900">{m.title}</div>
                <div className="text-xs text-gray-500 font-dm-sans truncate">{m.summary || "—"}</div>
              </div>
              <span className="text-xs text-gray-500 font-dm-sans">{m.lesson_count || 0} lessons</span>
              {m.is_premium && <Lock size={14} className="text-amber-500" />}
              <button onClick={() => { setDefaultModuleId(m.module_id); setEditingLesson(null); setShowLessonEditor(true); }}
                className="text-xs font-dm-sans font-bold text-blue-600 hover:underline"
                data-testid={`add-lesson-${m.slug}`}>
                + Lesson
              </button>
              <button onClick={() => { setEditingModule(m); setShowModuleEditor(true); }} className="text-gray-500 hover:text-gray-900 p-1" data-testid={`edit-module-${m.slug}`}>
                <Edit3 size={15} />
              </button>
              <button onClick={() => deleteModule(m)} className="text-gray-500 hover:text-red-600 p-1" data-testid={`delete-module-${m.slug}`}>
                <Trash2 size={15} />
              </button>
            </div>
            {expanded[m.module_id] && (
              <div className="border-t border-gray-200 bg-gray-50">
                {(lessonsByModule[m.module_id] || []).length === 0 ? (
                  <div className="px-12 py-4 text-sm text-gray-500 font-dm-sans">No lessons yet</div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {(lessonsByModule[m.module_id] || []).map((l) => (
                      <div key={l.lesson_id} className="flex items-center gap-3 pl-12 pr-5 py-3 hover:bg-white transition-colors">
                        <span className="text-[10px] font-mono font-bold text-gray-400 w-8">L{String(l.order).padStart(2, "0")}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-dm-sans font-medium text-gray-900 truncate">{l.title}</div>
                          {l.tags?.length > 0 && (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {l.tags.slice(0, 4).map((t) => (
                                <span key={t} className="text-[10px] font-dm-sans text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] font-dm-sans text-gray-500">+{l.xp_reward} XP</span>
                        {l.is_premium && <Lock size={12} className="text-amber-500" />}
                        <button onClick={() => { setEditingLesson(l); setShowLessonEditor(true); }} className="text-gray-500 hover:text-gray-900 p-1" data-testid={`edit-lesson-${l.slug}`}>
                          <Edit3 size={14} />
                        </button>
                        <button onClick={() => deleteLesson(l)} className="text-gray-500 hover:text-red-600 p-1" data-testid={`delete-lesson-${l.slug}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {!loading && modules.length === 0 && (
          <div className="text-center py-14 bg-white border border-gray-200 rounded-lg">
            <BookOpen size={32} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-dm-sans text-sm">No modules yet. Click "Add Module" to start.</p>
          </div>
        )}
      </div>

      <ModuleEditor open={showModuleEditor} item={editingModule} onClose={() => setShowModuleEditor(false)} onSaved={loadModules} />
      <LessonEditor
        open={showLessonEditor}
        item={editingLesson}
        modules={modules}
        defaultModuleId={defaultModuleId}
        onClose={() => setShowLessonEditor(false)}
        onSaved={() => { loadModules(); if (defaultModuleId) loadLessons(defaultModuleId); }}
      />
    </div>
  );
}
